import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import OverlayLoading from '../../../../../../Components/OverlayLoading';
import Form from '../../../../../../Components/Form/Form';
import InputField from '../../../../../../Components/Form/components/InputField';
import SubmitButton from '../../../../../../Components/Button/SubmitButton';
import withForm from '../../../../../../Components/Form/withForm';
import { NCZIPatientContact } from '../../../../Interfaces/ncziPatientInformations';
import NCZIAddress, {
	fillAddressDefaultValues,
	NCZIAddressValidator,
} from '../../../NCZIAddress';
import { WritePatientSummaryContactInformation } from '../../../../Commands/WritePatientSummaryContactInformation';
import { NCZIReferenceSelectItem } from '../../../ReferenceSearchSelect/NCZIReferenceSearchSelect';
import NCZICommandResult from '../../../NCZICommandResult/NCZICommandResult';
import useNCZICommandProcess from '../../../../Hooks/useNCZICommandProcess';
import InputWrapper from '../../../../../../Components/Form/components/InputWrapper/InputWrapper';

export interface NCZIEditContactFormValues {
	email: string;
	phone: string;
	note: string;
	address: {
		street: string;
		descriptiveNumber: string;
		city: NCZIReferenceSelectItem;
		country: NCZIReferenceSelectItem;
		postCode: NCZIReferenceSelectItem;
		region: NCZIReferenceSelectItem;
	};
}

interface Props {
	onClose?: (needReload: boolean) => void;
	contactInformation?: NCZIPatientContact;
	patientIdentifier: string;
}

const NCZIEditContactForm: React.FC<Props> = props => {
	const { onClose, patientIdentifier, contactInformation } = props;
	const [defaultValues, setDefaultValues] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const { processingCommandId, processCommand, isProcessing } =
		useNCZICommandProcess({
			onFinish: () => onClose?.(true),
		});

	const handleSubmit = async (rawValues: any) => {
		const values: NCZIEditContactFormValues =
			rawValues as NCZIEditContactFormValues;
		const finalValues = {
			email: values.email || undefined,
			phone: values.phone || undefined,
			note: values.note || undefined,
			address: values.address.street
				? {
						street: values.address.street,
						descriptiveNumber: values.address.descriptiveNumber,
						city: values.address.city.value,
						country: values.address.country.value,
						postCode: values.address.postCode.value,
						region: values.address.region.value,
						district: undefined,
						guideNumber: undefined,
				  }
				: undefined,
		};

		const writeCommand = WritePatientSummaryContactInformation({
			patientCode: patientIdentifier,
			patientInformation: finalValues,
		});

		await processCommand(writeCommand);
	};

	const validationSchema = yup.object({
		email: yup.string().nullable(),
		phone: yup.string().nullable(),
		note: yup.string().max(4000, 'nczi.validation.max4000').nullable(),
		address: NCZIAddressValidator.nullable().default(null),
	});

	useEffect(() => {
		setDefaultValues({
			email: contactInformation?.email,
			phone: contactInformation?.phone,
			note: contactInformation?.note,
			address: fillAddressDefaultValues(contactInformation?.address),
		});
		setIsLoading(false);
	}, []);

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	return (
		<div>
			{isLoading && <OverlayLoading />}
			{!isLoading && (
				<Form defaultValues={defaultValues} schema={validationSchema}>
					<InputWrapper>
						<InputField
							label='base.shared.email'
							name='email'
							isRequired={false}
						/>
					</InputWrapper>
					<InputWrapper>
						<InputField
							label='base.shared.phone'
							name='phone'
							isRequired={false}
						/>
					</InputWrapper>
					<InputWrapper>
						<InputField
							label='base.internalNote'
							name='note'
							isRequired={false}
							charactersLimit={4000}
						/>
					</InputWrapper>
					<NCZIAddress />
					<div className='mt-4'>
						<SubmitButton
							title='component.nczi.setup.save'
							onSubmit={handleSubmit}
						/>
					</div>
				</Form>
			)}
		</div>
	);
};

export default withForm(NCZIEditContactForm);
